import React, { useCallback, useEffect, useState } from 'react'
import classNames from 'classnames'
import { When } from 'react-if'
import { LinkButton } from '@/atoms/Button'
import { CircleCloseIcon } from '@/atoms/Icons/CircleCloseIcon'
import { CloseIcon } from '@/atoms/Icons/CloseIcon'
import { Image } from '@/atoms/Image'
import { ResponsiveBackgroundImage } from '@/atoms/ResponsiveBackgroundImage'
import { CaptionSM, LabelSM, TitleXXS } from '@/atoms/Text'
import { paths } from '@/constants/paths'
import { slugs } from '@/constants/slugs'
import { ExperimentVariation, GBFadeInExperiment } from '@/experimentation'
import { getWebClient } from '@/services/ApolloClient'
import { useSaleBanner } from '@/services/ContentfulSaleService/hooks'
import { useGuildMember, useUserGuildTickets } from '@/services/GuildUserService'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { useSafeTrack } from '@/utils/analytics'
import { getBooleanFromLocalStorage, writeToLocalStorage } from '@/utils/local-storage'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { useTranslate } from '@/utils/translate/translate-client'

export const TicketsNavBanner: React.FC = () => {
  const track = useSafeTrack()
  const { t } = useTranslate('common')
  const router = useSafeRouter()
  const { loading: bannerLoading, saleWithBanner } = useSaleBanner()
  const { isGuildMember, loading } = useGuildMember()
  const client = getWebClient()
  const [shouldRender, setShouldRender] = useState(false)
  const { numberAvailable: guildTicketsCount = 0, loading: loadingGuildTickets } = useUserGuildTickets({
    theatricalSlug: slugs.david,
    client,
    skip: !isGuildMember,
  })

  useEffect(() => {
    const hasDismissedBanner = getBooleanFromLocalStorage('hasDismissedDavidNavBanner')
    const isGuildJoinPage = router.pathname.includes('/guild/join')
    const isGuildPricingPage = router.pathname.includes('/guild/pricing')

    if (!hasDismissedBanner && !isGuildJoinPage && !isGuildPricingPage) {
      if (!loading && !bannerLoading && (isGuildMember || !saleWithBanner)) {
        setShouldRender(true)
      }
    }
  }, [router.pathname, isGuildMember, loading, bannerLoading, saleWithBanner])

  const handleTicketsNavBannerDismiss = useCallback(
    (buttonOrigin: boolean, e?: React.MouseEvent) => {
      if (e) {
        e.preventDefault()
        e.stopPropagation()
      }
      writeToLocalStorage('hasDismissedDavidNavBanner', true)
      if (!buttonOrigin) {
        track('Tickets Nav Banner Dismissed')
      }
      setShouldRender(false)
    },
    [track],
  )

  const handleTicketsNavButtonClick = useCallback(() => {
    track('Tickets Nav Banner Button Clicked')
    handleTicketsNavBannerDismiss(true)
  }, [handleTicketsNavBannerDismiss, track])

  return (
    <When condition={shouldRender}>
      <GBFadeInExperiment experimentName="david-nav-banner-pif-tickets" defaultValue={'tickets'}>
        <ExperimentVariation variation="tickets">
          <a
            className={classNames(
              'fixed cursor-pointer md:absolute inset-x-0 bottom-0 md:bottom-auto md:top-0 z-40 md:z-50 flex flex-col items-center max-w-full group-floating-cta bg-[#010D2E]',
            )}
            onClick={handleTicketsNavButtonClick}
            href={`${paths.tickets.index}/${slugs.david}`}
          >
            <div className="responsive relative h-14 w-full max-w-[1728px] p-3 md:h-16 md:p-4">
              <ResponsiveBackgroundImage
                src={getCloudinaryImageUrl({
                  path: '/v1761769740/angel-studios/nav/Promotional-Banner-BG-Mobile-Image-01_2.webp',
                  width: 1024,
                })}
                className="absolute inset-0 h-full w-full lg:hidden"
              />
              <ResponsiveBackgroundImage
                src={getCloudinaryImageUrl({
                  path: '/v1761768402/angel-studios/nav/Promotional-Banner-DAVID-BG-Desk-Image-01_2.webp',
                  width: 1728,
                })}
                className="absolute inset-0 hidden h-full w-full bg-cover bg-no-repeat lg:block"
              />
              <div className="relative mx-auto flex h-full w-full max-w-[1728px] items-center justify-between">
                <div className="flex flex-row md:gap-[27px]">
                  <div className="flex flex-col items-start gap-2 lg:flex-row lg:items-center lg:gap-[27px]">
                    <Image
                      src={getCloudinaryImageUrl({
                        path: '/v1761674532/angel-studios/nav/DAVID_MOVIE_David_Logo_MAIN_LOGO_ENG_2025_10_1.webp',
                        width: 129,
                      })}
                      alt="DAVID Horizontal Logo"
                      className="max-h-[16px] max-w-[64px] md:max-h-[32px] md:max-w-[129px]"
                      width={129}
                      height={32}
                    />
                    <CaptionSM
                      weight="bold"
                      className="lg:photon-title-xs !text-[#E4975D] [text-shadow:_0_1px_2px_rgb(0_0_0_/_80%)] lg:[text-shadow:none]"
                    >
                      {t('ticketsAvailableNow', 'Tickets Available Now')}
                    </CaptionSM>
                  </div>
                  <Image
                    src={getCloudinaryImageUrl({
                      path: '/v1761674708/angel-studios/nav/Promo_Tickets.webp',
                      width: 104,
                    })}
                    alt="DAVID Tickets"
                    className="hidden md:block"
                    width={104}
                    height={48}
                  />
                </div>
                <div className="flex flex-row items-center gap-[35px] md:gap-[42px] lg:gap-[145px]">
                  <div>
                    <When condition={!loadingGuildTickets}>
                      <LinkButton
                        href={`${paths.tickets.index}/${slugs.david}`}
                        variant={'black'}
                        className="rounded-lg border-transparent bg-white/20 px-4 py-2 hover:bg-white/40"
                        onClick={handleTicketsNavButtonClick}
                      >
                        <LabelSM>
                          {guildTicketsCount && guildTicketsCount > 0
                            ? t('claimTickets', 'Claim Tickets')
                            : t('getShowtimes', 'Get Showtimes')}
                        </LabelSM>
                      </LinkButton>
                    </When>
                  </div>
                  <div
                    onClick={(e) => handleTicketsNavBannerDismiss(false, e)}
                    className="cursor-pointer rounded-full lg:rounded-none"
                  >
                    <div className="hidden cursor-pointer lg:flex lg:flex-row lg:items-center lg:gap-2">
                      <TitleXXS weight="semibold" color="core-gray-400">
                        {t('close', 'Close')}
                      </TitleXXS>
                      <CloseIcon color="core-gray-400" size={24} className="cursor-pointer" />
                    </div>
                    <div className="lg:hidden">
                      <CircleCloseIcon color="core-gray-400" size={21} className="cursor-pointer" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </a>
        </ExperimentVariation>
        <ExperimentVariation variation="pif">
          <a
            className={classNames(
              'fixed cursor-pointer md:absolute inset-x-0 bottom-0 md:bottom-auto md:top-0 z-40 md:z-50 flex flex-col items-center max-w-full group-floating-cta bg-[#010D2E]',
            )}
            onClick={handleTicketsNavButtonClick}
            href={`${paths.payItForward.index}/${slugs.david}`}
          >
            <div className="responsive relative h-14 w-full max-w-[1728px] p-3 md:h-16 md:p-4">
              <ResponsiveBackgroundImage
                src={getCloudinaryImageUrl({
                  path: '/v1761769740/angel-studios/nav/Promotional-Banner-BG-Mobile-Image-01_2.webp',
                  width: 1024,
                })}
                className="absolute inset-0 h-full w-full lg:hidden"
              />
              <ResponsiveBackgroundImage
                src={getCloudinaryImageUrl({
                  path: '/v1761768402/angel-studios/nav/Promotional-Banner-DAVID-BG-Desk-Image-01_2.webp',
                  width: 1728,
                })}
                className="absolute inset-0 hidden h-full w-full bg-cover bg-no-repeat lg:block"
              />
              <div className="relative mx-auto flex h-full w-full max-w-[1728px] items-center justify-between">
                <div className="flex flex-row md:gap-[27px]">
                  <div className="flex flex-col items-start gap-2 lg:flex-row lg:items-center lg:gap-[27px]">
                    <Image
                      src={getCloudinaryImageUrl({
                        path: '/v1761674532/angel-studios/nav/DAVID_MOVIE_David_Logo_MAIN_LOGO_ENG_2025_10_1.webp',
                        width: 129,
                      })}
                      alt="DAVID Horizontal Logo"
                      className="max-h-[16px] max-w-[64px] md:max-h-[32px] md:max-w-[129px]"
                      width={129}
                      height={32}
                    />
                    <CaptionSM
                      weight="bold"
                      className="lg:photon-title-xs !text-[#E4975D] [text-shadow:_0_1px_2px_rgb(0_0_0_/_80%)] lg:[text-shadow:none]"
                    >
                      {t('helpOneMillionWatchFree', 'Help 1 Million Kids Watch Free')}
                    </CaptionSM>
                  </div>
                  <Image
                    src={getCloudinaryImageUrl({
                      path: '/v1761674708/angel-studios/nav/Promo_Tickets.webp',
                      width: 104,
                    })}
                    alt="DAVID Tickets"
                    className="hidden md:block"
                    width={104}
                    height={48}
                  />
                </div>
                <div className="flex flex-row items-center gap-[35px] md:gap-[42px] lg:gap-[145px]">
                  <div>
                    <When condition={!loadingGuildTickets}>
                      <LinkButton
                        href={`${paths.payItForward.index}/${slugs.david}`}
                        variant={'black'}
                        className="rounded-lg border-transparent bg-white/20 px-4 py-2 hover:bg-white/40"
                        onClick={handleTicketsNavButtonClick}
                      >
                        <LabelSM>{t('giftTickets', 'Gift Tickets')}</LabelSM>
                      </LinkButton>
                    </When>
                  </div>
                  <div
                    onClick={(e) => handleTicketsNavBannerDismiss(false, e)}
                    className="cursor-pointer rounded-full lg:rounded-none"
                  >
                    <div className="hidden cursor-pointer lg:flex lg:flex-row lg:items-center lg:gap-2">
                      <TitleXXS weight="semibold" color="core-gray-400">
                        {t('close', 'Close')}
                      </TitleXXS>
                      <CloseIcon color="core-gray-400" size={24} className="cursor-pointer" />
                    </div>
                    <div className="lg:hidden">
                      <CircleCloseIcon color="core-gray-400" size={21} className="cursor-pointer" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </a>
        </ExperimentVariation>
      </GBFadeInExperiment>

      {/* Spacer to push the site nav below the fixed banner on md+ */}
      <div className="hidden h-16 w-full md:block" aria-hidden="true" />
    </When>
  )
}
